import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Header from './Header';
import {
  api,
  BulkTokenSyncStatisticsItem,
  HourlyAdminModCumulativeResponse,
} from '../api/af-social-tracker-api';
import Plotly from 'plotly.js-dist';
import { useDataStore } from '../stores/data/dataStore';
import { TokenStatus } from '../stores/data/types';
import { useUIStore } from '../stores/ui/uiStore';
import CopyButton from './CopyButton';
import StatusChip from './StatusChip';
import CategoryChip from './CategoryChip';
import getApiErrorMessage from '../utils/getApiErrorMessage';
import { formatTimestampToLocaleString } from '../utils/time';
import { formatNumberToLargestUnit } from '../utils/formatters';


const formatSyncUntil = (ms: number | null | undefined): string => {
  if (ms == null || !Number.isFinite(ms)) {
    return '—';
  }
  const s = formatTimestampToLocaleString(ms);
  return s || '—';
};

const safeFormatNumber = (value: number | null | undefined): string => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return '0';
  }
  return parsed.toLocaleString('en-US');
};

const safeFormatMc = (value: number | null | undefined): string => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return '0';
  }
  return formatNumberToLargestUnit(parsed);
};

const renderDeltaInfo = (
  abs: number | null | undefined,
  pct: number | null | undefined,
) => {
  const absNum = Number(abs ?? 0);
  const pctNum = pct == null ? null : Number(pct);

  const absText = `${absNum > 0 ? '+' : ''}${safeFormatNumber(absNum)}`;
  const pctText =
    pctNum == null
      ? '0%'
      : `${pctNum > 0 ? '+' : ''}${Math.round(pctNum)}%`;

  const toneClass =
    absNum > 0 ? 'text-green-400' : absNum < 0 ? 'text-red-400' : 'text-gray-500';

  return <span className={`text-[10px] ${toneClass}`}>{absText} ({pctText})</span>;
};

const shortenAddress = (address: string) =>
  address.length > 12 ? `${address.slice(0, 6)}…${address.slice(-4)}` : address;

const openExternalInNewTab = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

const LINE_COLORS = [
  '#60a5fa',
  '#34d399',
  '#f472b6',
  '#f59e0b',
  '#a78bfa',
  '#22d3ee',
  '#f87171',
  '#facc15',
  '#2dd4bf',
  '#c084fc',
];
const MAX_SELECTED_TOKENS = 5;
const HOUR_MS = 60 * 60 * 1000;

const formatKstHourLabel = (timestampMs: number): string => {
  return new Date(timestampMs).toLocaleString('sv-SE', {
    timeZone: 'Asia/Seoul',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const buildForwardCumulativeSeries = (latestToOlderCumulative: number[]): number[] => {
  if (latestToOlderCumulative.length === 0) {
    return [];
  }

  const windowCounts: number[] = [];
  for (let i = 0; i < latestToOlderCumulative.length; i += 1) {
    const current = latestToOlderCumulative[i] ?? 0;
    const previous = i === 0 ? 0 : latestToOlderCumulative[i - 1] ?? 0;
    windowCounts.push(current - previous);
  }

  const oldestToLatestWindowCounts = [...windowCounts].reverse();
  const forwardCumulative: number[] = [];
  let running = 0;
  for (const count of oldestToLatestWindowCounts) {
    running += count;
    forwardCumulative.push(running);
  }

  return forwardCumulative;
};

type SortKey =
  | 'status'
  | 'category'
  | 'currentMc'
  | 'lastCommunityCollectedUntilMs'
  | 'totalTweets'
  | 'tweetsInTwoHoursAgo'
  | 'tweetsInLastHour'
  | 'totalAdminTweets'
  | 'adminTweetsInTwoHoursAgo'
  | 'adminTweetsInLastHour'
  | 'totalModTweets'
  | 'modTweetsInTwoHoursAgo'
  | 'modTweetsInLastHour'
  | 'totalHighFollowerTweets'
  | 'highFollowerTweetsInTwoHoursAgo'
  | 'highFollowerTweetsInLastHour'
  | 'uniqueHighFollowerAuthors'
  | 'uniqueHighFollowerAuthorsInTwoHoursAgo'
  | 'uniqueHighFollowerAuthorsInLastHour';

type SortDirection = 'asc' | 'desc';

const SocialPage: React.FC = () => {
  const isInitialized = useDataStore((s) => s.isInitialized);
  const { setSelectedToken, openSocialDetailModal } = useUIStore();
  const [rows, setRows] = useState<BulkTokenSyncStatisticsItem[]>([]);
  const [requestedCount, setRequestedCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('totalTweets');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [checkedByTokenAddress, setCheckedByTokenAddress] = useState<
    Record<string, boolean>
  >({});
  const [hourlyCumulativeByTokenAddress, setHourlyCumulativeByTokenAddress] =
    useState<Record<string, HourlyAdminModCumulativeResponse>>({});
  const adminChartRef = useRef<HTMLDivElement | null>(null);
  const modChartRef = useRef<HTMLDivElement | null>(null);

  const loadStats = useCallback(async () => {
    if (!isInitialized) return;

    const map = useDataStore.getState().tokenStateMap;
    const tokenAddresses = Object.values(map)
      .filter((t) => {
        const status = t.feTokenState?.status;
        const isBuyOrHold = status === TokenStatus.BUY || status === TokenStatus.HOLD;
        return isBuyOrHold;
      })
      .map((t) => t.tokenAddress);

    setRequestedCount(tokenAddresses.length);

    if (tokenAddresses.length === 0) {
      setRows([]);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const res = await api.getBulkTokenSyncStatistics(tokenAddresses);
      if (!res.success || res.data === undefined) {
        setRows([]);
        setError(res.message || '통계 조회에 실패했습니다.');
        return;
      }
      setRows(res.data);
    } catch (e) {
      setRows([]);
      setError(getApiErrorMessage(e, '통계를 불러오지 못했습니다.'));
    } finally {
      setIsLoading(false);
    }
  }, [isInitialized]);

  useEffect(() => {
    void loadStats();
  }, [loadStats]);

  const tokenStateMap = useDataStore((s) => s.tokenStateMap);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === 'desc' ? 'asc' : 'desc'));
      return;
    }
    setSortKey(key);
    setSortDirection('desc');
  };

  const sortedRows = useMemo(() => {
    const getSortValue = (row: BulkTokenSyncStatisticsItem, key: SortKey) => {
      const token = tokenStateMap[row.tokenAddress];
      if (key === 'status') {
        return token?.feTokenState?.status || '';
      }
      if (key === 'category') {
        return token?.feTokenState?.category || '';
      }
      if (key === 'currentMc') {
        return token?.statistics?.lastFlow?.mc ?? -1;
      }
      return row[key] ?? -1;
    };

    const sorted = [...rows].sort((a, b) => {
      const left = getSortValue(a, sortKey);
      const right = getSortValue(b, sortKey);

      if (typeof left === 'string' || typeof right === 'string') {
        const result = String(left).localeCompare(String(right));
        return sortDirection === 'desc' ? -result : result;
      }

      const diff = Number(left) - Number(right);
      return sortDirection === 'desc' ? -diff : diff;
    });
    return sorted;
  }, [rows, sortDirection, sortKey, tokenStateMap]);

  const sortIndicator = (key: SortKey) => {
    if (sortKey !== key) return ' ';
    return sortDirection === 'desc' ? '↓' : '↑';
  };

  const handleDetailsClick = (tokenAddress: string) => {
    const token = tokenStateMap[tokenAddress];
    setSelectedToken({
      tokenAddress,
      symbol: token?.tokenInfo?.tokenSymbol || 'Unknown',
    });
    openSocialDetailModal();
  };

  const handleTokenCheck = useCallback(
    async (row: BulkTokenSyncStatisticsItem, checked: boolean) => {
      setCheckedByTokenAddress((prev) => ({
        ...prev,
        [row.tokenAddress]: checked,
      }));

      if (!checked) {
        setHourlyCumulativeByTokenAddress((prev) => {
          const next = { ...prev };
          delete next[row.tokenAddress];
          return next;
        });
        return;
      }

      if (!row.tweetSyncId || !row.lastCommunityCollectedUntilMs) {
        return;
      }

      try {
        const res = await api.getHourlyAdminModCumulative(
          row.tweetSyncId,
          row.lastCommunityCollectedUntilMs,
          168,
        );
        const data = res.data;
        if (!res.success || !data) {
          return;
        }
        setHourlyCumulativeByTokenAddress((prev) => ({
          ...prev,
          [row.tokenAddress]: data,
        }));
      } catch (e) {
        console.error(
          `[SocialPage] hourly admin/mod cumulative fetch failed: ${row.tokenAddress}`,
          e,
        );
      }
    },
    [],
  );

  const selectedSeries = useMemo(() => {
    return sortedRows
      .filter((row) => checkedByTokenAddress[row.tokenAddress])
      .map((row) => {
        const token = tokenStateMap[row.tokenAddress];
        const symbol = token?.tokenInfo?.tokenSymbol || shortenAddress(row.tokenAddress);
        const payload = hourlyCumulativeByTokenAddress[row.tokenAddress];
        return {
          key: row.tokenAddress,
          label: `${symbol} (${shortenAddress(row.tokenAddress)})`,
          payload,
        };
      })
      .filter((row) => Boolean(row.payload));
  }, [checkedByTokenAddress, hourlyCumulativeByTokenAddress, sortedRows, tokenStateMap]);

  const selectedCount = useMemo(
    () => Object.values(checkedByTokenAddress).filter(Boolean).length,
    [checkedByTokenAddress],
  );

  useEffect(() => {
    const el = adminChartRef.current;
    if (!el) return;
    if (selectedSeries.length === 0) {
      Plotly.purge(el);
      return;
    }

    const traces = selectedSeries.map((series, idx) => {
      const data = series.payload?.adminCumulativeTotals ?? [];
      const anchor = series.payload?.anchorTimestampMs ?? Date.now();
      const y = buildForwardCumulativeSeries(data);
      const x = y.map((_, i) =>
        new Date(anchor - (y.length - 1 - i) * HOUR_MS).toISOString(),
      );
      const kstLabels = y.map((_, i) =>
        formatKstHourLabel(anchor - (y.length - 1 - i) * HOUR_MS),
      );
      return {
        type: 'scatter',
        mode: 'lines',
        x,
        y,
        name: series.label,
        customdata: kstLabels,
        hovertemplate:
          '%{fullData.name}<br>KST %{customdata}<br>Value %{y}<extra></extra>',
        line: {
          width: 2,
          color: LINE_COLORS[idx % LINE_COLORS.length],
        },
      };
    });

    Plotly.newPlot(
      el,
      traces as any,
      {
        title: { text: 'Admin Cumulative (hourly, up to 7d)', font: { color: '#e5e7eb', size: 14 } },
        paper_bgcolor: 'rgba(0,0,0,0)',
        plot_bgcolor: 'rgba(17,24,39,0.45)',
        font: { color: '#d1d5db', size: 11 },
        margin: { l: 48, r: 56, t: 40, b: 38 },
        xaxis: {
          title: 'Date (KST)',
          type: 'date',
          tickformat: '%m/%d %H:%M',
          tickangle: -35,
          gridcolor: 'rgba(107,114,128,0.25)',
          zerolinecolor: 'rgba(107,114,128,0.3)',
        },
        yaxis: {
          title: 'Cumulative Tweets',
          side: 'right',
          automargin: true,
          gridcolor: 'rgba(107,114,128,0.25)',
          zerolinecolor: 'rgba(107,114,128,0.3)',
        },
        legend: {
          orientation: 'h',
          y: -0.25,
          x: 0,
          font: { size: 10, color: '#d1d5db' },
        },
      } as any,
      { displayModeBar: false, responsive: true },
    );

    return () => {
      Plotly.purge(el);
    };
  }, [selectedSeries]);

  useEffect(() => {
    const el = modChartRef.current;
    if (!el) return;
    if (selectedSeries.length === 0) {
      Plotly.purge(el);
      return;
    }

    const traces = selectedSeries.map((series, idx) => {
      const data = series.payload?.modCumulativeTotals ?? [];
      const anchor = series.payload?.anchorTimestampMs ?? Date.now();
      const y = buildForwardCumulativeSeries(data);
      const x = y.map((_, i) =>
        new Date(anchor - (y.length - 1 - i) * HOUR_MS).toISOString(),
      );
      const kstLabels = y.map((_, i) =>
        formatKstHourLabel(anchor - (y.length - 1 - i) * HOUR_MS),
      );
      return {
        type: 'scatter',
        mode: 'lines',
        x,
        y,
        name: series.label,
        customdata: kstLabels,
        hovertemplate:
          '%{fullData.name}<br>KST %{customdata}<br>Value %{y}<extra></extra>',
        line: {
          width: 2,
          color: LINE_COLORS[idx % LINE_COLORS.length],
        },
      };
    });

    Plotly.newPlot(
      el,
      traces as any,
      {
        title: { text: 'Mod Cumulative (hourly, up to 7d)', font: { color: '#e5e7eb', size: 14 } },
        paper_bgcolor: 'rgba(0,0,0,0)',
        plot_bgcolor: 'rgba(17,24,39,0.45)',
        font: { color: '#d1d5db', size: 11 },
        margin: { l: 48, r: 56, t: 40, b: 38 },
        xaxis: {
          title: 'Date (KST)',
          type: 'date',
          tickformat: '%m/%d %H:%M',
          tickangle: -35,
          gridcolor: 'rgba(107,114,128,0.25)',
          zerolinecolor: 'rgba(107,114,128,0.3)',
        },
        yaxis: {
          title: 'Cumulative Tweets',
          side: 'right',
          automargin: true,
          gridcolor: 'rgba(107,114,128,0.25)',
          zerolinecolor: 'rgba(107,114,128,0.3)',
        },
        legend: {
          orientation: 'h',
          y: -0.25,
          x: 0,
          font: { size: 10, color: '#d1d5db' },
        },
      } as any,
      { displayModeBar: false, responsive: true },
    );

    return () => {
      Plotly.purge(el);
    };
  }, [selectedSeries]);

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header title="Social" showPageControls={false} />
      <div className="w-full max-w-[2300px] mx-auto py-8 px-4">
        {!isInitialized && (
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 text-gray-300">
            Loading Social Stats...
          </div>
        )}

        {isInitialized && requestedCount === 0 && !isLoading && (
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 text-gray-300">
            Buy/Hold 토큰이 없습니다.
          </div>
        )}

        {isInitialized && requestedCount > 0 && (
          <>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 text-sm text-gray-400">
              <span>
                Buy/Hold 토큰 {requestedCount}개 중 트윗 싱크 통계가
                있는 토큰 {rows.length}개
              </span>
              <span className="text-xs text-gray-500">
                비교 선택: {selectedCount}/{MAX_SELECTED_TOKENS}
              </span>
              <button
                type="button"
                onClick={() => void loadStats()}
                disabled={isLoading}
                className="rounded-md border border-gray-600 bg-gray-800 px-3 py-1.5 text-gray-200 hover:bg-gray-700 disabled:opacity-50"
              >
                {isLoading ? '불러오는 중…' : '새로고침'}
              </button>
            </div>

            {error && (
              <div className="mb-4 rounded-lg border border-red-800 bg-red-950/40 px-4 py-3 text-red-200">
                {error}
              </div>
            )}

            {isLoading && rows.length === 0 && !error && (
              <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 text-gray-300">
                통계를 불러오는 중…
              </div>
            )}

            {!isLoading && rows.length === 0 && !error && requestedCount > 0 && (
              <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 text-gray-300">
                선택한 토큰에 대해 등록된 트윗 싱크가 없습니다.
              </div>
            )}

            {rows.length > 0 && (
              <div className="overflow-x-auto rounded-lg border border-gray-700 bg-gray-800">
                <table className="min-w-[2250px] w-full text-left text-sm">
                  <thead className="border-b border-gray-700 bg-gray-800/80 text-xs uppercase tracking-wide text-gray-400">
                    <tr className="border-b border-gray-700/60">
                      <th rowSpan={2} className="w-[40px] px-2 py-3 text-center align-middle">
                        <button
                          type="button"
                          onClick={() => {
                            setCheckedByTokenAddress({});
                            setHourlyCumulativeByTokenAddress({});
                          }}
                          className="text-gray-400 hover:text-gray-200 transition-colors"
                          title="전체 선택 해제"
                        >
                          Pick
                        </button>
                      </th>
                      <th rowSpan={2} className="w-[170px] px-2 py-3 align-middle">Token</th>
                      <th rowSpan={2} className="w-[86px] px-2 py-3 whitespace-nowrap text-center align-middle">
                        <button type="button" onClick={() => handleSort('status')}>
                          Status {sortIndicator('status')}
                        </button>
                      </th>
                      <th rowSpan={2} className="w-[92px] px-2 py-3 whitespace-nowrap text-center align-middle">
                        <button type="button" onClick={() => handleSort('category')}>
                          Category {sortIndicator('category')}
                        </button>
                      </th>
                      <th rowSpan={2} className="w-[94px] px-2 py-3 whitespace-nowrap text-center align-middle border-r border-gray-400">
                        <button type="button" onClick={() => handleSort('currentMc')}>
                          Current MC {sortIndicator('currentMc')}
                        </button>
                      </th>
                      <th colSpan={3} className="px-3 py-2 text-center border-r border-gray-400">Tweet Count</th>
                      <th colSpan={3} className="px-3 py-2 text-center border-r border-gray-400">Admin</th>
                      <th colSpan={3} className="px-3 py-2 text-center border-r border-gray-400">Mod</th>
                      <th colSpan={3} className="px-3 py-2 text-center border-r border-gray-400">5K+ Tweets</th>
                      <th colSpan={3} className="px-3 py-2 text-center border-r border-gray-400">5K+ Authors</th>
                      <th rowSpan={2} className="w-[110px] px-2 py-3 whitespace-nowrap text-center align-middle">
                        <button type="button" onClick={() => handleSort('lastCommunityCollectedUntilMs')}>
                          Last comm. sync {sortIndicator('lastCommunityCollectedUntilMs')}
                        </button>
                      </th>
                      <th rowSpan={2} className="px-3 py-3 text-center align-middle">Details</th>
                    </tr>
                    <tr>
                      <th className="px-3 py-2 text-center bg-gray-700/25">
                        <button type="button" onClick={() => handleSort('tweetsInTwoHoursAgo')}>
                          -2h {sortIndicator('tweetsInTwoHoursAgo')}
                        </button>
                      </th>
                      <th className="px-3 py-2 text-center bg-gray-700/40">
                        <button type="button" onClick={() => handleSort('tweetsInLastHour')}>
                          -1h {sortIndicator('tweetsInLastHour')}
                        </button>
                      </th>
                      <th className="px-3 py-2 text-center border-r border-gray-400">
                        <button type="button" onClick={() => handleSort('totalTweets')}>
                          Total {sortIndicator('totalTweets')}
                        </button>
                      </th>
                      <th className="px-3 py-2 text-center bg-gray-700/25">
                        <button type="button" onClick={() => handleSort('adminTweetsInTwoHoursAgo')}>
                          -2h {sortIndicator('adminTweetsInTwoHoursAgo')}
                        </button>
                      </th>
                      <th className="px-3 py-2 text-center bg-gray-700/40">
                        <button type="button" onClick={() => handleSort('adminTweetsInLastHour')}>
                          -1h {sortIndicator('adminTweetsInLastHour')}
                        </button>
                      </th>
                      <th className="px-3 py-2 text-center border-r border-gray-400">
                        <button type="button" onClick={() => handleSort('totalAdminTweets')}>
                          Total {sortIndicator('totalAdminTweets')}
                        </button>
                      </th>
                      <th className="px-3 py-2 text-center bg-gray-700/25">
                        <button type="button" onClick={() => handleSort('modTweetsInTwoHoursAgo')}>
                          -2h {sortIndicator('modTweetsInTwoHoursAgo')}
                        </button>
                      </th>
                      <th className="px-3 py-2 text-center bg-gray-700/40">
                        <button type="button" onClick={() => handleSort('modTweetsInLastHour')}>
                          -1h {sortIndicator('modTweetsInLastHour')}
                        </button>
                      </th>
                      <th className="px-3 py-2 text-center border-r border-gray-400">
                        <button type="button" onClick={() => handleSort('totalModTweets')}>
                          Total {sortIndicator('totalModTweets')}
                        </button>
                      </th>
                      <th className="px-3 py-2 text-center bg-gray-700/25">
                        <button type="button" onClick={() => handleSort('highFollowerTweetsInTwoHoursAgo')}>
                          -2h {sortIndicator('highFollowerTweetsInTwoHoursAgo')}
                        </button>
                      </th>
                      <th className="px-3 py-2 text-center bg-gray-700/40">
                        <button type="button" onClick={() => handleSort('highFollowerTweetsInLastHour')}>
                          -1h {sortIndicator('highFollowerTweetsInLastHour')}
                        </button>
                      </th>
                      <th className="px-3 py-2 text-center border-r border-gray-400">
                        <button type="button" onClick={() => handleSort('totalHighFollowerTweets')}>
                          Total {sortIndicator('totalHighFollowerTweets')}
                        </button>
                      </th>
                      <th className="px-3 py-2 text-center bg-gray-700/25">
                        <button type="button" onClick={() => handleSort('uniqueHighFollowerAuthorsInTwoHoursAgo')}>
                          -2h {sortIndicator('uniqueHighFollowerAuthorsInTwoHoursAgo')}
                        </button>
                      </th>
                      <th className="px-3 py-2 text-center bg-gray-700/40">
                        <button type="button" onClick={() => handleSort('uniqueHighFollowerAuthorsInLastHour')}>
                          -1h {sortIndicator('uniqueHighFollowerAuthorsInLastHour')}
                        </button>
                      </th>
                      <th className="px-3 py-2 text-center border-r border-gray-400">
                        <button type="button" onClick={() => handleSort('uniqueHighFollowerAuthors')}>
                          Total {sortIndicator('uniqueHighFollowerAuthors')}
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {sortedRows.map((row) => {
                      const token = tokenStateMap[row.tokenAddress];
                      const symbol = token?.tokenInfo?.tokenSymbol || '—';
                      const isChecked = Boolean(checkedByTokenAddress[row.tokenAddress]);
                      const isDisabled = !isChecked && selectedCount >= MAX_SELECTED_TOKENS;
                      return (
                        <tr key={row.tokenAddress} className="hover:bg-gray-700/40">
                          {/*
                            체크 시 backend hourly cumulative 결과를 row별로 캐싱.
                            현재는 선택 상태 기반의 API 트리거/캐시에 초점을 둠.
                          */}
                          <td className="w-[40px] px-2 py-2 text-center align-middle">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(event) =>
                                void handleTokenCheck(row, event.target.checked)
                              }
                              disabled={isDisabled}
                              title={
                                isDisabled
                                  ? `최대 ${MAX_SELECTED_TOKENS}개까지 선택할 수 있습니다.`
                                  : hourlyCumulativeByTokenAddress[row.tokenAddress]
                                    ? 'hourly cumulative loaded'
                                    : 'hourly cumulative not loaded'
                              }
                              className="h-4 w-4 rounded border-gray-500 bg-gray-800 text-blue-500 focus:ring-blue-500 disabled:opacity-40 disabled:cursor-not-allowed"
                            />
                          </td>
                          <td className="w-[170px] px-2 py-2">
                            <div className="flex flex-col gap-0.5">
                              <span className="font-medium text-white truncate" title={symbol}>{symbol}</span>
                              <div className="flex items-center gap-1 text-xs text-gray-400 min-w-0">
                                <span className="font-mono truncate" title={row.tokenAddress}>
                                  {shortenAddress(row.tokenAddress)}
                                </span>
                                <CopyButton text={row.tokenAddress} />
                                <button
                                  type="button"
                                  title="Axiom"
                                  onClick={() => {
                                    const poolAddress = token?.tokenInfo?.poolAddress;
                                    if (!poolAddress) return;
                                    openExternalInNewTab(`https://axiom.trade/meme/${poolAddress}`);
                                  }}
                                  className="inline-flex items-center justify-center disabled:opacity-50"
                                  disabled={!token?.tokenInfo?.poolAddress}
                                >
                                  <img src="/axiom.png" alt="axiom" className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  type="button"
                                  title="GMGN"
                                  onClick={() => {
                                    openExternalInNewTab(
                                      `https://gmgn.ai/sol/token/solscan_${row.tokenAddress}`,
                                    );
                                  }}
                                  className="inline-flex items-center justify-center"
                                >
                                  <img src="/gmgn.png" alt="gmgn" className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          </td>
                          <td className="w-[86px] px-2 py-2 whitespace-nowrap text-center">
                            <StatusChip
                              tokenAddress={row.tokenAddress}
                              status={(token?.feTokenState?.status as TokenStatus | undefined) ?? TokenStatus.NONE}
                              onSuccess={() => void loadStats()}
                            />
                          </td>
                          <td className="w-[92px] px-2 py-2 whitespace-nowrap text-center">
                            <CategoryChip category={token?.feTokenState?.category} />
                          </td>
                          <td className="w-[94px] px-2 py-2 text-center tabular-nums border-r border-gray-400">
                            <span
                              className={
                                (token?.statistics?.lastFlow?.mc ?? 0) >= 500000
                                  ? 'text-amber-300'
                                  : 'text-white'
                              }
                            >
                              {safeFormatMc(token?.statistics?.lastFlow?.mc)}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-center tabular-nums bg-gray-700/25">
                            <div className="flex flex-col items-center leading-tight">
                              <span>{safeFormatNumber(row.tweetsInTwoHoursAgo)}</span>
                              {renderDeltaInfo(row.tweetsInTwoHoursAgoDeltaAbs, row.tweetsInTwoHoursAgoDeltaPct)}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center tabular-nums bg-gray-700/40">
                            <div className="flex flex-col items-center leading-tight">
                              <span>{safeFormatNumber(row.tweetsInLastHour)}</span>
                              {renderDeltaInfo(row.tweetsInLastHourDeltaAbs, row.tweetsInLastHourDeltaPct)}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center tabular-nums text-white border-r border-gray-400">
                            {safeFormatNumber(row.totalTweets)}
                          </td>
                          <td className="px-3 py-2 text-center tabular-nums bg-gray-700/25">
                            <div className="flex flex-col items-center leading-tight">
                              <span>{safeFormatNumber(row.adminTweetsInTwoHoursAgo)}</span>
                              {renderDeltaInfo(
                                row.adminTweetsInTwoHoursAgoDeltaAbs,
                                row.adminTweetsInTwoHoursAgoDeltaPct,
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center tabular-nums bg-gray-700/40">
                            <div className="flex flex-col items-center leading-tight">
                              <span>{safeFormatNumber(row.adminTweetsInLastHour)}</span>
                              {renderDeltaInfo(
                                row.adminTweetsInLastHourDeltaAbs,
                                row.adminTweetsInLastHourDeltaPct,
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center tabular-nums border-r border-gray-400">
                            {safeFormatNumber(row.totalAdminTweets)}
                          </td>
                          <td className="px-3 py-2 text-center tabular-nums bg-gray-700/25">
                            <div className="flex flex-col items-center leading-tight">
                              <span>{safeFormatNumber(row.modTweetsInTwoHoursAgo)}</span>
                              {renderDeltaInfo(row.modTweetsInTwoHoursAgoDeltaAbs, row.modTweetsInTwoHoursAgoDeltaPct)}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center tabular-nums bg-gray-700/40">
                            <div className="flex flex-col items-center leading-tight">
                              <span>{safeFormatNumber(row.modTweetsInLastHour)}</span>
                              {renderDeltaInfo(row.modTweetsInLastHourDeltaAbs, row.modTweetsInLastHourDeltaPct)}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center tabular-nums border-r border-gray-400">
                            {safeFormatNumber(row.totalModTweets)}
                          </td>
                          <td className="px-3 py-2 text-center tabular-nums bg-gray-700/25">
                            <div className="flex flex-col items-center leading-tight">
                              <span>{safeFormatNumber(row.highFollowerTweetsInTwoHoursAgo)}</span>
                              {renderDeltaInfo(
                                row.highFollowerTweetsInTwoHoursAgoDeltaAbs,
                                row.highFollowerTweetsInTwoHoursAgoDeltaPct,
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center tabular-nums bg-gray-700/40">
                            <div className="flex flex-col items-center leading-tight">
                              <span>{safeFormatNumber(row.highFollowerTweetsInLastHour)}</span>
                              {renderDeltaInfo(
                                row.highFollowerTweetsInLastHourDeltaAbs,
                                row.highFollowerTweetsInLastHourDeltaPct,
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center tabular-nums border-r border-gray-400">
                            {safeFormatNumber(row.totalHighFollowerTweets)}
                          </td>
                          <td className="px-3 py-2 text-center tabular-nums bg-gray-700/25">
                            <div className="flex flex-col items-center leading-tight">
                              <span>{safeFormatNumber(row.uniqueHighFollowerAuthorsInTwoHoursAgo)}</span>
                              {renderDeltaInfo(
                                row.uniqueHighFollowerAuthorsInTwoHoursAgoDeltaAbs,
                                row.uniqueHighFollowerAuthorsInTwoHoursAgoDeltaPct,
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center tabular-nums bg-gray-700/40 ">
                            <div className="flex flex-col items-center leading-tight">
                              <span>{safeFormatNumber(row.uniqueHighFollowerAuthorsInLastHour)}</span>
                              {renderDeltaInfo(
                                row.uniqueHighFollowerAuthorsInLastHourDeltaAbs,
                                row.uniqueHighFollowerAuthorsInLastHourDeltaPct,
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center tabular-nums border-r border-gray-400">
                            {safeFormatNumber(row.uniqueHighFollowerAuthors)}
                          </td>
                          <td
                            className="w-[120px] pl-4 pr-2 py-2 text-xs text-gray-300 whitespace-nowrap text-center"
                            title={
                              row.lastCommunityCollectedUntilMs != null
                                ? String(row.lastCommunityCollectedUntilMs)
                                : undefined
                            }
                          >
                            {formatSyncUntil(row.lastCommunityCollectedUntilMs)}
                          </td>
                          <td className="pl-2px-3 py-2 text-center">
                            <button
                              type="button"
                              onClick={() => handleDetailsClick(row.tokenAddress)}
                              className="rounded-md border border-gray-600 bg-gray-800 px-3 py-1 text-xs text-gray-200 hover:bg-gray-700"
                            >
                              Details
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {selectedSeries.length > 0 && (
              <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="rounded-lg border border-gray-700 bg-gray-800 p-3">
                  <div ref={adminChartRef} className="w-full h-[380px]" />
                </div>
                <div className="rounded-lg border border-gray-700 bg-gray-800 p-3">
                  <div ref={modChartRef} className="w-full h-[380px]" />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SocialPage;
